import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { navigate, useStaticQuery, graphql } from 'gatsby';
import { defaultSearchParams, paramNames } from 'config/search';
import { SideBarContext, initialSideBarState } from 'context/sidebarContextWrapper';
import { SET_DEFAULT_SEARCH, SearchDispatchContext } from 'context/searchContextWrapper'; 
import { APP_STUDIO_TAG, FUSION_AI_TAG, FUSION_CONNECTORS_TAG, FUSION_SERVER_TAG, FUSION_TAG } from 'config/productItems';
import FocusTrap from 'focus-trap-react';
import DropdownIcon from 'images/icons/dropdown.inline.svg';
import SearchIcon from 'images/icons/search.inline.svg';
import useSearch from 'hooks/useSearch';
import SearchBarInput from './SearchBarInput.component';
import './searchbar.styles.scss';

const LOC_RECENT_SEARCH = 'L-recentSearchLegacy';
const LOC_SEARCH_HISTORY = 'L-search-historyLegacy';

const TypeAheadDropdown = React.lazy(() => import('./TypeAheadDropdown.component'))
const isBrowser = typeof window !== "undefined"

function SearchBar({ 
  location,
  toggleSearchBar,
  setToggleSearchBar
}){

  // const dataTrending = useStaticQuery(graphql`
  //   query {
  //     allTrendingSearches(limit: 3) {
  //       edges {
  //         node {
  //           query
  //         }
  //       }
  //     }
  //   }
  // `)

  const state = useContext(SideBarContext) || initialSideBarState;
  const dispatch = useContext(SearchDispatchContext);

  const [recent, setRecent] = useState([]);
  const [isHistoryOpen, setisHistoryOpen] = useState(false);
  const [searchParams, setSearchParams] = useState('');
  const [pinned, setPinned] = useState([])
  const [results, setResults] = useState([]);
  const [typeaheadResults, setTypeaheadResults] = useState([])
  const [toggleOpen, setToggleOpen] = useState(false);
  const [defaultVal, setDefaultVal] = useState('');
  const [focusIndex, setFocusIndex] = useState(-1);
  
  const [isShowingPlaceholder, setIsShowingPlaceholder] = useState(false);
  
  const inputRef = useRef();
  const dropdownRef = useRef();
  
  const [inputLength, setInputLength] = useState(inputRef.current?.value.length)

  const { data: dataTypeahead } = useSearch(searchParams, 'typeahead', state.currentProduct)
  const { data } = useSearch(searchParams, 'searchResults')

  const setPlaceholderValue = useCallback(
    () => {
      setIsShowingPlaceholder(true);
      dispatch({
        type: SET_DEFAULT_SEARCH,
        payload: 'Search',
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (inputRef.current.value.length === 0) {
      setPlaceholderValue();
    }
  }, [setPlaceholderValue]);

  useEffect(() => {
    setRecent(JSON.parse(localStorage.getItem(LOC_RECENT_SEARCH)) || []);
  }, []);

  useEffect(() => {
    setToggleOpen(false);
    if (location?.search) {
      const searchCheckParams = new URLSearchParams(location.search);
      const queryParam = searchCheckParams.get('q') || '';
      inputRef.current.value = queryParam;
      setDefaultVal(queryParam);
    } else if (location?.pathname !== '/search' || location === undefined) {
      setPlaceholderValue();
    }
  }, [location, setPlaceholderValue]);

  useEffect(()=>{
    if(toggleSearchBar){
        inputRef.current.focus()
        setToggleOpen(true);
    }
}, [toggleSearchBar, inputRef, setToggleOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data?.response?.docs) {
      setResults(data?.response?.docs);
    }
    if(data?.fusion?.pinned_values){
      setPinned(data?.fusion?.pinned_values)
    } else {
      setPinned([])
    }
    if (dataTypeahead?.response?.docs) {
      setTypeaheadResults(dataTypeahead?.response?.docs.slice(0,3));
    }
  }, [data, dataTypeahead ]);


  if(inputRef.current?.value.length !== inputLength){
    setInputLength(inputRef.current.value.length)
    setFocusIndex(-1)
  }

  const handleChange = value => {
    // Create params for Fusion with value
    if(value.length >= 3){
      const typeAheadQuery = { ...defaultSearchParams.params, wt: 'json', q: value };
      const typeAheadParams = `?${new URLSearchParams(typeAheadQuery).toString()}`;
      setSearchParams(typeAheadParams);
    } else if (value.length === 0){
      setSearchParams('')
      setResults([])
    }
  };

  // Debounce user input with time specified in ms
  const debounceOnChange = useCallback(debounce(handleChange, 200), []); // eslint-disable-line react-hooks/exhaustive-deps

  const routeQuery = useCallback(( status ) => {
    setToggleSearchBar(false)
    if (inputRef.current.value.length > 0) {


      const query = inputRef.current.value;
      const params = { ...defaultSearchParams.params, wt: 'json', q: query };
      let url = '/search?' + new URLSearchParams(params).toString(); /* eslint-disable-line prefer-template */

      switch(state.currentProduct.tag){
        case FUSION_TAG:
          url += `&fq=productName:("${state.currentProduct.tag}","${FUSION_CONNECTORS_TAG}")&fq=${paramNames.version}:("${state.currentVersion}")`
          break;
        case FUSION_SERVER_TAG:
          url += `&fq=productName:("${state.currentProduct.tag}","${FUSION_CONNECTORS_TAG}","${FUSION_AI_TAG}","${APP_STUDIO_TAG}")&fq=${paramNames.version}:("${state.currentVersion}")`
          break;
        case FUSION_CONNECTORS_TAG:
          url += `&fq=productName:("${state.currentProduct.tag}","${FUSION_TAG}","${FUSION_SERVER_TAG}","${FUSION_AI_TAG}")&fq=${paramNames.version}:("${state.currentVersion}")`
          break;
        default:
          break;
      }

      let newRecent = [...recent];

      if (recent.includes(query)) newRecent.splice(recent.indexOf(query), 1); 
      
      localStorage.setItem(LOC_SEARCH_HISTORY, JSON.stringify([query, ...newRecent]));
      if (newRecent.length >= 5) newRecent.pop();

      newRecent = [query, ...newRecent];

      setRecent(newRecent);
      localStorage.setItem(LOC_RECENT_SEARCH, JSON.stringify(newRecent));

      dispatch({
        type: SET_DEFAULT_SEARCH,
        payload: query,
      });
      
      setToggleOpen(false);
      inputRef.current.blur();

      if(status === 'trending') {navigate(`/search?sort=&start=0&rows=10&q=${query}`); return }
      navigate(url);
    }
  }, [dispatch, recent, setToggleSearchBar, state.currentProduct.tag, state.currentVersion]);

  const handleKeyDown = event => {
    // escape search dropdown modal
    if(event.keyCode === 27){
      inputRef.current.blur();
      setToggleOpen(false)
    }
    if(event.keyCode === 38){ // arrow up
      event.preventDefault()
      setFocusIndex(prev => prev - 1)
    }
    if(event.keyCode === 40){ // arrow down
      setFocusIndex(prev => prev + 1)
    }
    if(event.keyCode === 13 && focusIndex !== -1){ // focusIndex !== -1 means enter is on input box value/focus
      setFocusIndex(44)
      return
    }
    if (event.key === 'Enter' && focusIndex !== 44) {
      routeQuery();
    }
  };

  let keyOS
  if(isBrowser){
    keyOS = (window.navigator?.userAgentData?.platform === 'Windows') || (window.navigator?.platform === 'Win32') ? 'ctrl' : '⌘'
  }

  return (
    <>
      <div className={toggleOpen ? 'cover-dark' : 'cover-clear'} style={{transition: 'background-color 100ms ease'}} />
      <FocusTrap active={toggleOpen}>
        <div className="headerSearch">
          <SearchBarInput 
            location={location}
            ref={inputRef}
            isShowingPlaceholder={isShowingPlaceholder}
            defaultVal={defaultVal}
            debounceOnChange={debounceOnChange}
            handleKeyDown={handleKeyDown}
            setToggleOpen={setToggleOpen}
            focusIndex={focusIndex}
            setFocusIndex={setFocusIndex}
          />

          <button
            aria-label="Search Query"
            type="submit"
            className="searchBar-btn"
            onClick={() => routeQuery()}
          >
            <span className="search-icon">
              <SearchIcon />
            </span>
          </button>


          {keyOS && <>
            <span
            className="search-shortcut"
            style={{right: '73px', paddingLeft: '4px'}}
            >
              {keyOS}
            </span>

            <span
              className="search-shortcut"
              style={{right: '45px', paddingLeft: '7px'}}
            >
              K
            </span>
          </>}
        

          <button
            ref={dropdownRef}
            aria-label="Search Suggestions Dropdown"
            type="button"
            className={`${toggleOpen ? 'searchBar-btn-active ' : ''}${
              results.length === 0 &&
              recent.length === 0 &&
              inputRef.current?.value.length > 0 &&
              !isHistoryOpen
                ? 'hide '
                : ''
            }searchBar-btn-suggestions`}
            onClick={() => {setToggleOpen(e => !e); inputRef.current.focus()}}
          >
            <DropdownIcon />
          </button>

          <React.Suspense fallback="">
            <TypeAheadDropdown 
              toggleOpen={toggleOpen}
              pinned={pinned}
              results={results}
              typeaheadResults={typeaheadResults}
              // trendingResults={dataTrending.allTrendingSearches.edges.map(itm => itm.node)}  
              inputRef={inputRef}
              isHistoryOpen={isHistoryOpen}
              setisHistoryOpen={setisHistoryOpen}
              dropdownRef={dropdownRef}
              recent={recent}
              setRecent={setRecent}
              routeQuery={routeQuery}
              LOC_RECENT_SEARCH={LOC_RECENT_SEARCH}
              setToggleOpen={setToggleOpen}
              focusIndex={focusIndex}
              setFocusIndex={setFocusIndex}
              toggleSearchBar={toggleSearchBar}
              setToggleSearchBar={setToggleSearchBar}
            />
          </React.Suspense>
        </div>
      </FocusTrap>
    </>
  );
};

SearchBar.propTypes = {
  location: PropTypes.object.isRequired,
  toggleSearchBar: PropTypes.bool.isRequired,
  setToggleSearchBar: PropTypes.func.isRequired
};

export default SearchBar;
